import { getCurrentFacilityId } from "@risalabs_frontend_org/auth-wrapper";
import { Modal } from "@risalabs_frontend_org/oasis-ui-kit";
import { useEffect, useMemo, useState } from "react";
import { GeneralOrderDetails } from "../../../data-model/medicalPaOrdersModel";
import { ModalId } from "../../../enums/modalId";
import { useSiteDetailsFieldOptions } from "./hooks/useSiteDetailsFieldOptions";
import { useSiteDetailsSave } from "./hooks/useSiteDetailsSave";
import { selectedNpiHasMultipleAddresses } from "./siteDetailsFieldOptionsHelper";
import SiteDetailsForm from "./siteDetailsForm";
import { SiteDetailsFieldOptions } from "./siteDetailsForm.config";
import {
  EMPTY_SITE_DETAILS_FORM,
  isInternalSiteType,
  isSiteDetailsFormValid,
  SiteDetailsFormValues,
} from "./siteDetailsForm.types";

/** Props for the Add Site NPI modal. */
interface AddSiteNpiModalProps {
  /** Called when the modal is dismissed to sync Redux modal state. */
  onClose: () => void;
  /** Emits the saved form payload after a successful upsert. */
  onSave?: (values: SiteDetailsFormValues) => void;
  /** Optional callback after org test locations are refreshed. */
  onSubmissionUpdated?: () => Promise<void>;
  /** Optional dropdown options per form field. */
  fieldOptions?: SiteDetailsFieldOptions;
  /** Initial form values when editing an existing site. */
  initialValues?: SiteDetailsFormValues;
  /**
   * Reloads the Postgres `/order` document.
   * `success` is false when the reload fails.
   */
  fetchGeneralOrderDetails?: (
    orderId: string,
    orgId: string,
    options?: { hideLoader?: boolean },
  ) => Promise<{ success: boolean }>;
  /** Optional general order details. */
  generalOrderDetails?: GeneralOrderDetails;
}

/**
 * Modal for adding or editing site NPI / location details on an order.
 *
 * Save upserts the org test location catalog, writes the selected type onto
 * medical_pa_order_status.npi_type, and refreshes cached dropdown options.
 * The multiple-address warning follows the NPI selected in the form.
 *
 * @param props.onClose - Clears the opened modal id in Redux.
 * @param props.onSave - Receives all field values after a successful upsert.
 * @param props.onSubmissionUpdated - Optional callback after org locations refresh.
 * @param props.initialValues - Pre-filled values from submission test location data.
 * @param props.fieldOptions - Optional per-field SearchableCard option overrides.
 */
const AddSiteNpiModal = ({
  onClose,
  onSave,
  onSubmissionUpdated,
  fieldOptions = {},
  initialValues = EMPTY_SITE_DETAILS_FORM,
  fetchGeneralOrderDetails,
  generalOrderDetails,
}: AddSiteNpiModalProps) => {
  const [formValues, setFormValues] =
    useState<SiteDetailsFormValues>(initialValues);
  const {
    fieldOptions: sliceFieldOptions,
    locations,
    loading: isCatalogLoading,
  } = useSiteDetailsFieldOptions(formValues.npi);
  const mergedFieldOptions = useMemo(
    () => ({ ...sliceFieldOptions, ...fieldOptions }),
    [fieldOptions, sliceFieldOptions],
  );
  const hasMultipleAddresses = useMemo(
    () => selectedNpiHasMultipleAddresses(formValues.npi, locations),
    [formValues.npi, locations],
  );

  useEffect(() => {
    setFormValues(initialValues);
  }, [initialValues]);

  const isFormValid = useMemo(
    () => isSiteDetailsFormValid(formValues),
    [formValues],
  );
  const isInternal = isInternalSiteType(formValues.type);
  const { isSaving, handleSave } = useSiteDetailsSave({
    onClose,
    onSaveSuccess: onSave,
    onSubmissionUpdated,
    fetchOrder: fetchGeneralOrderDetails,
    orderId: generalOrderDetails?.order_id ?? "",
    orgId: getCurrentFacilityId() ?? "",
  });

  const handleModalSave = () => {
    void handleSave(formValues);
  };

  return (
    <Modal
      dialogId={ModalId.ADD_SITE_NPI_MODAL}
      onSave={handleModalSave}
      onClose={onClose}
      title="Site Details"
      children={
        <SiteDetailsForm
          values={formValues}
          onChange={setFormValues}
          options={mergedFieldOptions}
          locations={locations}
          loading={isCatalogLoading}
          hasMultipleAddresses={hasMultipleAddresses}
        />
      }
      saveButtonText={isSaving ? "Saving..." : "Save"}
      cancelText="Cancel"
      disableSave={
        !isFormValid || isSaving || (!isInternal && isCatalogLoading)
      }
      maxHeightPercentage={80}
    />
  );
};

export default AddSiteNpiModal;
